import { entityTypeHasPipeline } from '@/lib/crm/hooks/useStages';
import type { CrmObject } from '@/types/crm';

/** Entity type name for a minted credential's principal. */
export const SERVICE_ACCOUNT_ENTITY_TYPE = 'ahoy_service_account';

/** Association type name linking a credential's principal to its creator. */
export const SERVICE_ACCOUNT_CREATOR_ASSOCIATION =
  'ahoy_service_account_to_creator';

/**
 * Width of the create/edit credential slideouts. Wider than the 600px default
 * because every grant row carries a label, a scope control and a write toggle,
 * which wrapped onto two lines at the default; resizable within these bounds so
 * an org with long type labels can widen it further. The minimum is the
 * narrowest width at which the grant list's three columns still fit with the
 * widest scope control shown (org reach) and the label column at its 8rem
 * floor — see api-access-grant-layout.ts.
 */
export const CREDENTIAL_SLIDEOUT_WIDTH = {
  initial: 680,
  min: 560,
  max: 800,
} as const;

/**
 * Header rhythm for the credential slideouts. The chrome's close button is a
 * 36px hit target around a 20px glyph, so with the header's default padding
 * the glyph sits 8px inside the edge that the body's list frame and its
 * "Select all / Clear" controls align to — and the two-line subtitle pushes
 * a vertically-centred button down beside the subtitle. Trim the header's
 * right padding by that 8px and align the row to the top so the X sits on
 * the content edge, level with the title.
 */
export const CREDENTIAL_SLIDEOUT_HEADER = {
  align: 'start',
  className: 'pr-2 md:pr-4',
} as const;

/** Display name of a credential row, with the same fallback everywhere it is shown. */
export function getCredentialName(credential: CrmObject): string {
  return (
    (credential.properties?.['ahoy_display_name']?.value as
      | string
      | undefined) ?? 'Untitled credential'
  );
}

/**
 * How far a credential's READ grants extend across the org's users. Stored on
 * `ahoy_service_account.ahoy_reach`; absent (every credential minted before
 * reach existed, and every user-path mint) reads as `user`.
 *
 * - `user` — the creator's own access: org-visible records plus, at most, their
 *   own private ones. Minted through the ordinary events path, bounded by the
 *   engine's non-escalation rule.
 * - `org` — a "global" key: every user's private records of the granted types.
 *   No longer minted by this UI; carried only by credentials created before the
 *   create form became creator-bounded.
 */
export type CredentialReach = 'user' | 'org';

export const REACH_PROPERTY = 'ahoy_reach';

/** The permission group gating write access to the service-account settings section (mirrors the backend gate). */
export const SERVICE_ACCOUNT_ADMIN_GROUP = 'ahoy_other_admin';

/** Reach of a stored credential; anything but a stored `org` is `user` (fail closed, like the backend). */
export function credentialReachOf(credential: CrmObject): CredentialReach {
  return credential.properties?.[REACH_PROPERTY]?.value === 'org'
    ? 'org'
    : 'user';
}

/**
 * The ontology kind a grantable type is listed under (docs/claude/ENTITY_KINDS.md
 * § "The five ontology kinds"). Picker rows are grouped by it so a long list
 * scans as "people, deals, activity" rather than one flat run of checkboxes.
 */
export type GrantableGroup = 'party' | 'case' | 'asset' | 'activity' | 'other';

/** Group order in the picker: the durable stocks first, then the work that moves over them. */
export const GRANTABLE_GROUP_ORDER: readonly GrantableGroup[] = [
  'party',
  'case',
  'asset',
  'activity',
  'other',
];

export const GRANTABLE_GROUP_LABEL: Record<GrantableGroup, string> = {
  party: 'People & companies',
  // TODO(copy): 'cases' is an engine kind name (ahoy-ux-copy engine-vocabulary
  // rule); needs a customer-facing group name from design before rename.
  case: 'Deals & cases',
  // TODO(copy): 'Assets' is an engine kind name (ahoy-ux-copy engine-vocabulary
  // rule); needs a customer-facing group name from design before rename.
  asset: 'Assets',
  activity: 'Activity',
  other: 'Other',
};

/** `person` is the marker that composes with `party`; a person-marked type without an explicit ontology kind still belongs with people. */
function grantableGroupOf(entityKind: readonly string[]): GrantableGroup {
  for (const group of GRANTABLE_GROUP_ORDER) {
    if (group !== 'other' && entityKind.includes(group)) {
      return group;
    }
  }
  return entityKind.includes('person') ? 'party' : 'other';
}

export interface GrantableType {
  entityTypeName: string;
  label: string;
  group: GrantableGroup;
}

interface GrantableObjectType {
  entity_type_name: string;
  label: string;
  label_plural: string;
  entity_kind: string[];
  /** See `CrmObjectTypeSchema.writable_event_types`; missing means every write event. */
  writable_event_types?: string[];
}

/**
 * Types every service account needs regardless of what was ticked.
 *
 * The three meta types gate schema introspection: a service account resolves
 * its permissions from its own `ahoy_permissions` and never inherits
 * `ahoy_default`, so unlike a user it does NOT hold the universal floor that
 * makes these reads free. Without them it cannot call the schema endpoints at
 * all, and every granted type is a bag of unlabelled ids.
 *
 * `ahoy_user` is here because owner/creator associations target it, and
 * association types are only visible when BOTH endpoints are readable (see
 * issue #9666) — so a missing `ahoy_user` read does not merely leave an
 * unresolvable id, it deletes `*_to_owner` from the schema entirely.
 *
 * `ahoy_org` is the target of every `*_visible_to_org` scoping association,
 * which the same both-endpoint rule would otherwise hide.
 */
const ALWAYS_INCLUDED_TYPES = [
  'ahoy_entity_type',
  'ahoy_property_type',
  'ahoy_association_type',
  'ahoy_user',
  'ahoy_org',
] as const;

/**
 * Pipeline and stage type names derived from a case-kind type.
 *
 * Name derivation (not type discrimination) is the sanctioned use of the
 * `{base}_pipeline` / `{base}_pipeline_stage` convention — see
 * docs/claude/ENTITY_KINDS.md.
 */
function pipelineTypeNamesFor(type: GrantableObjectType): string[] {
  const hasPipeline = entityTypeHasPipeline(type.entity_type_name, [type]);
  if (!hasPipeline) {
    return [];
  }
  return [
    `${type.entity_type_name}_pipeline`,
    `${type.entity_type_name}_pipeline_stage`,
  ];
}

/**
 * The extra types a selection needs to be usable, beyond the types ticked.
 *
 * The picker only offers party/asset/case/activity kinds, so none of these are
 * reachable through it — which is why they are derived rather than left to the
 * admin to remember.
 *
 * Filtered to types that (a) exist in this org and (b) the creating admin can
 * read. Both filters are load-bearing: a grant naming a nonexistent type is
 * dead weight, and one the admin does not hold is rejected outright by the
 * non-escalation validator, which would fail the whole mint rather than this
 * one grant.
 */
export function deriveImpliedTypes(
  selectedTypeNames: readonly string[],
  allObjectTypes: readonly GrantableObjectType[],
  canRead: (entityTypeName: string) => boolean
): string[] {
  const byName = new Map(allObjectTypes.map(t => [t.entity_type_name, t]));
  const implied = new Set<string>(ALWAYS_INCLUDED_TYPES);

  for (const name of selectedTypeNames) {
    const type = byName.get(name);
    if (type) {
      for (const pipelineType of pipelineTypeNamesFor(type)) {
        implied.add(pipelineType);
      }
    }
  }

  const selected = new Set(selectedTypeNames);
  return [...implied]
    .filter(name => !selected.has(name))
    .filter(name => byName.has(name))
    .filter(canRead)
    .sort();
}

export function getGrantableTypes(
  objectTypes: GrantableObjectType[],
  allowedKinds: string[],
  canRead: (entityTypeName: string) => boolean
): GrantableType[] {
  return objectTypes
    .filter(
      objectType =>
        objectType.entity_kind.some(kind => allowedKinds.includes(kind)) &&
        canRead(objectType.entity_type_name)
    )
    .map(objectType => ({
      entityTypeName: objectType.entity_type_name,
      // Plural: a grant covers every record of the type, not one record.
      // "Contacts" reads as the set being granted; "Contact" reads as a single
      // record and understates the scope.
      label: objectType.label_plural || objectType.label,
      group: grantableGroupOf(objectType.entity_kind),
    }));
}
